import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth, ShopProvider } from './contexts';
import { LoginForm } from './components/auth';
import { MainLayout } from './components/layout';
import { HomePage } from './pages/HomePage';
import { DashboardPage } from './pages/DashboardPage';
import { ProductsPage } from './pages/ProductsPage';
import { GeneratorPage } from './pages/GeneratorPage';
import { PromptsPage } from './pages/PromptsPage';
import { HistoryPage } from './pages/HistoryPage';
import AutomationsPage from './pages/AutomationsPage';
import AutomationDetailPage from './pages/AutomationDetailPage';
import CreateAutomationPage from './pages/CreateAutomationPage';
import { AdminSettingsPage } from './pages/AdminSettingsPage';
import { AdminUsersPage } from './pages/AdminUsersPage';
import { Loader2, LogOut, RefreshCw, Clock } from 'lucide-react';
import { signOut } from 'firebase/auth';
import { auth } from './firebase';

// Loading screen with timeout and recovery options
function LoadingScreen() {
    const [showOptions, setShowOptions] = useState(false);
    const [isClearing, setIsClearing] = useState(false);

    useEffect(() => {
        // Show recovery options after 5 seconds
        const timer = setTimeout(() => {
            setShowOptions(true);
        }, 5000);
        return () => clearTimeout(timer);
    }, []);

    const handleClearSession = async () => {
        setIsClearing(true);
        try {
            await signOut(auth);
            // Clear any cached data
            localStorage.clear();
            sessionStorage.clear();
            // Reload the page
            window.location.reload();
        } catch (error) {
            console.error('Error clearing session:', error);
            window.location.reload();
        }
    };

    const handleRefresh = () => {
        window.location.reload();
    };

    return (
        <div className="loading-screen">
            <Loader2 className="animate-spin" size={48} />
            {showOptions && (
                <div className="loading-options animate-fadeIn">
                    <p>Le chargement prend plus de temps que prévu...</p>
                    <div className="loading-buttons">
                        <button onClick={handleRefresh} className="loading-btn">
                            <RefreshCw size={16} />
                            Rafraîchir
                        </button>
                        <button onClick={handleClearSession} disabled={isClearing} className="loading-btn loading-btn-danger">
                            <LogOut size={16} />
                            {isClearing ? 'Déconnexion...' : 'Réinitialiser la session'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

// Redirect component that properly handles dynamic route parameters
function AutomationRedirect() {
    const { jobId } = useParams<{ jobId: string }>();
    return <Navigate to={`/apps/image-generator/automations/${jobId}`} replace />;
}

// Pending approval screen for unapproved users
function PendingApprovalScreen() {
    const { signOut: logOut, userProfile } = useAuth();
    const [isLoggingOut, setIsLoggingOut] = useState(false);

    const handleLogout = async () => {
        setIsLoggingOut(true);
        try {
            await logOut();
            // Force redirect to login page
            window.location.href = '/login';
        } catch (error) {
            console.error('Error logging out:', error);
            setIsLoggingOut(false);
        }
    };

    return (
        <div className="pending-approval-screen">
            <div className="pending-approval-card glass">
                <div className="pending-icon">
                    <Clock size={64} />
                </div>
                <h1>Compte en attente d'approbation</h1>
                <p>
                    Bonjour <strong>{userProfile?.displayName || 'Utilisateur'}</strong>,<br />
                    Votre compte a été créé avec succès, mais un administrateur doit approuver votre accès avant que vous puissiez utiliser le service.
                </p>
                <p className="pending-email">Vous serez notifié dès que votre accès sera approuvé.</p>
                <button onClick={handleLogout} disabled={isLoggingOut} className="logout-btn">
                    <LogOut size={18} />
                    {isLoggingOut ? 'Déconnexion...' : 'Se déconnecter'}
                </button>
            </div>
        </div>
    );
}

// Protected route without layout (for HomePage)
function ProtectedRouteNoLayout({ children }: { children: React.ReactNode }) {
    const { userProfile, isLoading, isAdmin, isApproved } = useAuth();

    if (isLoading) {
        return <LoadingScreen />;
    }

    if (!userProfile) {
        return <Navigate to="/login" replace />;
    }

    // Check if user is approved (admins are always approved)
    if (!isApproved && !isAdmin) {
        return <PendingApprovalScreen />;
    }

    return <>{children}</>;
}

// Protected route with MainLayout (for app pages)
function ProtectedRoute({ children, adminOnly = false }: { children: React.ReactNode; adminOnly?: boolean }) {
    const { userProfile, isLoading, isAdmin, isApproved } = useAuth();

    if (isLoading) {
        return <LoadingScreen />;
    }

    if (!userProfile) {
        return <Navigate to="/login" replace />;
    }

    // Check if user is approved (admins are always approved)
    if (!isApproved && !isAdmin) {
        return <PendingApprovalScreen />;
    }

    if (adminOnly && !isAdmin) {
        return <Navigate to="/" replace />;
    }

    return <MainLayout>{children}</MainLayout>;
}

function AppRoutes() {
    const { userProfile, isLoading } = useAuth();

    if (isLoading) {
        return <LoadingScreen />;
    }

    return (
        <Routes>
            <Route
                path="/login"
                element={userProfile ? <Navigate to="/" replace /> : <LoginForm />}
            />

            {/* Portal Home */}
            <Route
                path="/"
                element={
                    <ProtectedRouteNoLayout>
                        <HomePage />
                    </ProtectedRouteNoLayout>
                }
            />

            {/* Image Generator App Routes */}
            <Route
                path="/apps/image-generator"
                element={
                    <ProtectedRoute>
                        <DashboardPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/apps/image-generator/products"
                element={
                    <ProtectedRoute>
                        <ProductsPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/apps/image-generator/generator"
                element={
                    <ProtectedRoute>
                        <GeneratorPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/apps/image-generator/prompts"
                element={
                    <ProtectedRoute>
                        <PromptsPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/apps/image-generator/history"
                element={
                    <ProtectedRoute>
                        <HistoryPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/apps/image-generator/automations"
                element={
                    <ProtectedRoute>
                        <AutomationsPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/apps/image-generator/automations/new"
                element={
                    <ProtectedRoute>
                        <CreateAutomationPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/apps/image-generator/automations/:jobId"
                element={
                    <ProtectedRoute>
                        <AutomationDetailPage />
                    </ProtectedRoute>
                }
            />

            {/* Legacy routes - redirect to new structure */}
            <Route path="/products" element={<Navigate to="/apps/image-generator/products" replace />} />
            <Route path="/generator" element={<Navigate to="/apps/image-generator/generator" replace />} />
            <Route path="/prompts" element={<Navigate to="/apps/image-generator/prompts" replace />} />
            <Route path="/history" element={<Navigate to="/apps/image-generator/history" replace />} />
            <Route path="/automations" element={<Navigate to="/apps/image-generator/automations" replace />} />
            <Route path="/automations/new" element={<Navigate to="/apps/image-generator/automations/new" replace />} />
            <Route path="/automations/:jobId" element={<AutomationRedirect />} />

            {/* Admin Routes */}
            <Route
                path="/admin/settings"
                element={
                    <ProtectedRoute adminOnly>
                        <AdminSettingsPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/users"
                element={
                    <ProtectedRoute adminOnly>
                        <AdminUsersPage />
                    </ProtectedRoute>
                }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

export default function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <ShopProvider>
                    <AppRoutes />
                </ShopProvider>
            </AuthProvider>
        </BrowserRouter>
    );
}
